// apps/web/src/features/dashai/lib/chatStoreClient.ts
//
// JSON client for the `dashaiChatStore` Firebase function.
// Action-dispatched CRUD for chat sessions, messages, feedback, and
// shared-chat reads.

import { postWithAuthRetry } from './onePSfaClient';

export interface SessionView {
  chat_id: string;
  title: string | null;
  country: string | null;
  filter_view_id: string | null;
  is_starred: boolean | null;
  share_token: string | null;
  id_client: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ChatMessageRow {
  id: string;
  role: string;
  message?: string;
  [k: string]: unknown;
}

export interface FeedbackRow {
  message_id: string;
  feedback_type: string;
  categories: string[];
  comment: string | null;
}

function url(): string {
  const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
  if (!base) throw new Error('Firebase functions URL not configured');
  return `${base.replace(/\/+$/, '')}/dashaiChatStore`;
}

// Chat-store CRUD ops (session/message reads + writes) are sub-second in the
// happy path. Cap them so a disconnected or hung backend surfaces as a
// rejection — which callers' error UIs handle — instead of an indefinitely
// pending request that strands the user on a spinner or welcome screen.
const CHAT_STORE_TIMEOUT_MS = 20_000;

async function call<T>(body: unknown): Promise<T> {
  // Race the request against a timeout so a hung connection rejects (→ the
  // caller's error UI) rather than pending forever. We don't AbortController the
  // fetch — that would change postWithAuthRetry's call signature; the dangling
  // request just completes ignored. The timer is cleared on settle so a
  // resolved request never trips a late rejection.
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('dashaiChatStore request timed out — the server did not respond.')),
      CHAT_STORE_TIMEOUT_MS,
    );
  });
  let res: Response;
  try {
    res = await Promise.race([postWithAuthRetry(url(), body), timeout]);
  } finally {
    clearTimeout(timer!);
  }
  if (!res.ok) {
    // Status code in the message is load-bearing: useSessionLookup classifies
    // notFound via /not found|404|403/i on this string — a bare body like
    // {"error":"forbidden"} matches nothing and renders the misleading
    // "server didn't respond" screen instead of "Not Found".
    const detail = await res.text();
    throw new Error(`dashaiChatStore failed (${res.status})${detail ? `: ${detail}` : ''}`);
  }
  return res.json() as Promise<T>;
}

export const chatStoreClient = {
  listSessions: (idClient?: string) =>
    call<{ sessions: SessionView[] }>({ action: 'list', id_client: idClient || undefined }),

  // The newest `limit` chats in one workspace, newest first.
  recentSessions: (idClient: string, limit: number) =>
    call<{ sessions: SessionView[] }>({ action: 'recent', id_client: idClient, limit }),

  getSession: (chatId: string) =>
    call<{ session: SessionView }>({ action: 'get', chat_id: chatId }),

  createSession: (
    chatId: string,
    idClient: string,
    // No workflow_id: the chat store derives it from the workflow-input message
    // and ignores the field here, so offering it would advertise a write that
    // does nothing.
    fields?: {
      title?: string;
      country?: string;
      filter_view_id?: string;
      is_starred?: boolean;
      share_token?: string;
      // Where the chat started. Absent means the DashAI chat page.
      source?: 'dashboard_panel';
    },
  ) =>
    call<{ ok: true; chat_id: string }>({
      action: 'create',
      chat_id: chatId,
      id_client: idClient,
      ...fields,
    }),

  renameSession: (chatId: string, title: string) =>
    call<{ ok: true }>({ action: 'rename', chat_id: chatId, title }),

  setShareToken: (chatId: string, shareToken: string) =>
    call<{ ok: true }>({ action: 'set-share-token', chat_id: chatId, share_token: shareToken }),

  deleteSession: (chatId: string) =>
    call<{ ok: true }>({ action: 'delete', chat_id: chatId }),

  // The server refuses a request without a workspace, so a delete never
  // reaches another workspace's chats.
  deleteNonStarred: (idClient: string) =>
    call<{ ok: true; count: number }>({ action: 'delete-non-starred', id_client: idClient }),

  setStarred: (chatId: string, isStarred: boolean) =>
    call<{ ok: true }>({ action: 'set-starred', chat_id: chatId, is_starred: isStarred }),

  loadHistory: (chatId: string) =>
    call<{ messages: ChatMessageRow[]; mapboxToken?: string }>({
      action: 'load-history',
      chat_id: chatId,
    }),

  storeMessage: (
    chatId: string,
    role: string,
    message: string,
    extras?: {
      id?: string;
      message_type?: string;
      chart_data?: unknown;
      map_data?: unknown;
      workflow_input?: unknown;
      workspace_id?: string;
    },
  ) =>
    call<{ ok: true; id: string }>({
      action: 'store',
      chat_id: chatId,
      role,
      message,
      ...extras,
    }),

  updateMessage: (chatId: string, messageId: string, patch: Record<string, unknown>) =>
    call<{ ok: true }>({ action: 'update', chat_id: chatId, message_id: messageId, patch }),

  submitFeedback: (
    chatId: string,
    messageId: string,
    feedbackType: string,
    opts?: { categories?: string[]; comment?: string },
  ) =>
    call<{ ok: true }>({
      action: 'submit',
      chat_id: chatId,
      message_id: messageId,
      feedback_type: feedbackType,
      ...opts,
    }),

  loadFeedback: (chatId: string) =>
    call<{ feedback: FeedbackRow[] }>({ action: 'list-feedback', chat_id: chatId }),

  removeFeedback: (chatId: string, messageId: string) =>
    call<{ ok: true }>({ action: 'remove-feedback', chat_id: chatId, message_id: messageId }),

  copyEvent: (chatId: string, messageId: string) =>
    call<{ ok: true }>({ action: 'copy-event', chat_id: chatId, message_id: messageId }),

  getShared: (shareToken: string) =>
    call<{ session: SessionView; messages: ChatMessageRow[] }>({
      action: 'get-shared',
      share_token: shareToken,
    }),
};
